import { DayData } from '../services/calendarService';

// Cache for calendar data
type CalendarCache = {
  [key: string]: {
    data: Record<string, DayData>;
    timestamp: number;
  };
};

// In-memory cache
const calendarCache: CalendarCache = {};

// Cache expiration time (30 minutes)
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

/**
 * Get calendar data from cache
 * @param year Year
 * @param month Month (0-11)
 * @returns Cached calendar data or null if not found or expired
 */
export const getCalendarDataFromCache = (
  year: number,
  month: number
): Record<string, DayData> | null => {
  const cacheKey = `${year}-${month}`;
  const cachedData = calendarCache[cacheKey];
  
  if (!cachedData) {
    return null;
  }
  
  // Check if cache is expired
  const now = Date.now();
  if (now - cachedData.timestamp > CACHE_EXPIRATION_MS) {
    delete calendarCache[cacheKey];
    return null;
  }
  
  return cachedData.data;
};

/**
 * Set calendar data in cache
 * @param year Year
 * @param month Month (0-11)
 * @param data Calendar data
 */
export const setCalendarDataInCache = (
  year: number,
  month: number,
  data: Record<string, DayData>
): void => {
  const cacheKey = `${year}-${month}`;
  calendarCache[cacheKey] = {
    data,
    timestamp: Date.now()
  };
};

/**
 * Clear calendar cache
 */
export const clearCalendarCache = (): void => {
  Object.keys(calendarCache).forEach(key => {
    delete calendarCache[key];
  });
};

/**
 * Format date to YYYY-MM-DD
 * @param date Date object
 * @returns Formatted date string
 */
export const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Get days in month
 * @param year Year
 * @param month Month (0-11)
 * @returns Number of days in month
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get day of week (0 = Sunday, 1 = Monday, etc.)
 * @param date Date object
 * @returns Day of week
 */
export const getDayOfWeek = (date: Date): number => {
  return date.getDay();
};

/**
 * Check if date is in range
 * @param date Date to check
 * @param startDate Start date
 * @param endDate End date
 * @returns True if date is in range
 */
export const isDateInRange = (
  date: string,
  startDate: string,
  endDate: string
): boolean => {
  return date >= startDate && date <= endDate;
};

/**
 * Create a safety timeout that will trigger if an operation takes too long
 * @param timeoutMs Timeout in milliseconds
 * @param onTimeout Callback to execute on timeout
 * @returns Timeout ID
 */
export const createSafetyTimeout = (
  timeoutMs: number,
  onTimeout: () => void
): NodeJS.Timeout => {
  return setTimeout(onTimeout, timeoutMs);
};

/**
 * Wrap a promise with a timeout
 * @param promise Promise to wrap
 * @param timeoutMs Timeout in milliseconds
 * @param errorMessage Error message to throw on timeout
 * @returns Promise with timeout
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string = 'Operation timed out'
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
    })
  ]);
};
